import { Flex, Text } from "@radix-ui/themes";
import { cn } from "@/lib/utils";

interface LoadingIndicatorProps {
  variant?: "spinner" | "dots" | "pulse" | "bars";
  size?: "sm" | "md" | "lg";
  text?: string;
  className?: string;
}

export const LoadingIndicator = ({
  variant = "spinner",
  size = "md",
  text,
  className,
}: LoadingIndicatorProps) => {
  const sizeClasses = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  };

  const textSizeClasses = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg",
  };

  const SpinnerVariant = () => (
    <svg
      className={cn("animate-spin text-blue-600", sizeClasses[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const DotsVariant = () => (
    <div className={cn("flex space-x-1", className)}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className={cn(
            "bg-blue-600 rounded-full animate-bounce",
            size === "sm"
              ? "w-1 h-1"
              : size === "md"
                ? "w-1.5 h-1.5"
                : "w-2 h-2",
          )}
          style={{
            animationDelay: `${i * 0.1}s`,
            animationDuration: "0.6s",
          }}
        />
      ))}
    </div>
  );

  const PulseVariant = () => (
    <div className={cn("relative", sizeClasses[size], className)}>
      <div className="absolute inset-0 bg-blue-600 rounded-full animate-ping opacity-75" />
      <div className="relative bg-blue-600 rounded-full h-full w-full" />
    </div>
  );

  const BarsVariant = () => (
    <div className={cn("flex items-end space-x-1", className)}>
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className={cn(
            "bg-blue-600 animate-pulse",
            size === "sm"
              ? "w-0.5 h-3"
              : size === "md"
                ? "w-1 h-4"
                : "w-1.5 h-6",
          )}
          style={{
            animationDelay: `${i * 0.15}s`,
            animationDuration: "0.8s",
          }}
        />
      ))}
    </div>
  );

  const renderVariant = () => {
    switch (variant) {
      case "dots":
        return <DotsVariant />;
      case "pulse":
        return <PulseVariant />;
      case "bars":
        return <BarsVariant />;
      default:
        return <SpinnerVariant />;
    }
  };

  return (
    <Flex direction="column" align="center" gap="3" className="select-none">
      {renderVariant()}
      {text && (
        <Text
          className={cn(
            "text-gray-600 font-medium animate-pulse",
            textSizeClasses[size],
          )}
        >
          {text}
        </Text>
      )}
    </Flex>
  );
};

// Helper component for full-screen loading overlay
export const FullScreenLoader = ({
  text = "Loading...",
  variant = "spinner",
}: Pick<LoadingIndicatorProps, "text" | "variant">) => (
  <Flex
    className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50"
    align="center"
    justify="center"
  >
    <LoadingIndicator variant={variant} size="lg" text={text} />
  </Flex>
);

// Helper component for inline loading
export const InlineLoader = ({
  text,
  variant = "dots",
  className,
}: Pick<LoadingIndicatorProps, "text" | "variant" | "className">) => (
  <Flex align="center" gap="2" className={className}>
    <LoadingIndicator variant={variant} size="sm" />
    {text && (
      <Text size="2" className="text-gray-600">
        {text}
      </Text>
    )}
  </Flex>
);
